<script>
import {
    parseLiveblogPayload,
    resolveTweetId,
    resolveTwitterAuthor,
    resolveTwitterUrl,
    resolveVideoPlayback,
    resolveVideoProviderLabel,
} from './liveblogMediaUtils'
import { buildEventTimelineLabels, resolveEventMetaFromItem } from './liveblogEventTypeMeta'

export default {
    name: 'LiveblogTimelineItem',
    props: {
        item: {
            type: Object,
            required: true,
        },
        active: {
            type: Boolean,
            default: false,
        },
        canReorder: {
            type: Boolean,
            default: false,
        },
        imageUrls: {
            type: Array,
            default: () => [],
        },
        imageUrl: {
            type: String,
            default: null,
        },
        eventTypes: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            galleryIndex: 0,
        }
    },
    computed: {
        isTwitterEntry() {
            return String(this.item.content_type || '').toLowerCase() === 'twitter'
        },
        isVideoEntry() {
            const type = String(this.item.content_type || '').toLowerCase()
            return type === 'video' || type === 'highlight'
        },
        entryPayload() {
            return parseLiveblogPayload(this.item.payload)
        },
        minuteLabel() {
            const m = this.item.event_minute
            if (!m) return '—'
            return String(m).endsWith("'") ? m : `${m}'`
        },
        isEventEntry() {
            return String(this.item.content_type || '').toLowerCase() === 'event'
        },
        eventTimelineLabels() {
            if (!this.isEventEntry) return null
            return buildEventTimelineLabels(this.item, this.eventTypes)
        },
        scoreLabel() {
            if (this.item.home_goals === null || this.item.home_goals === '') return ''
            return `${this.item.home_goals} - ${this.item.away_goals}`
        },
        displayScore() {
            if (!this.scoreLabel) return false
            if (this.isEventEntry) {
                return this.eventTimelineLabels ? this.eventTimelineLabels.showScore : false
            }
            return true
        },
        headline() {
            if (this.eventTimelineLabels) return this.eventTimelineLabels.headline
            if (this.isTwitterEntry) {
                if (this.twitterAuthor) return `Tweet — ${this.twitterAuthor}`
                if (this.twitterUrl) return 'Tweet'
            }
            if (this.isVideoEntry) {
                const label = this.videoProviderLabel || (this.isHighlightEntry ? 'Highlight' : 'Video')
                return label
            }
            if (this.item.title) return this.stripHtml(this.item.title)
            return this.item.content_type || 'Entry'
        },
        subline() {
            if (this.eventTimelineLabels) return this.eventTimelineLabels.subline
            const parts = []
            if (this.item.team_code) parts.push(this.item.team_code)
            if (this.item.period_name) parts.push(this.item.period_name)
            return parts.join(' · ')
        },
        bodyPreview() {
            if (this.isEventEntry) return ''
            if (!this.item.body) return ''
            return this.stripHtml(this.item.body)
        },
        showTypeBadge() {
            return !this.isEventEntry
        },
        typeBadgeLabel() {
            const type = String(this.item.content_type || '').toLowerCase()
            if (type === 'highlight') return 'Highlight'
            if (!type) return 'Entry'
            return type.charAt(0).toUpperCase() + type.slice(1)
        },
        eventMeta() {
            if (this.item.content_type !== 'event') return null
            return resolveEventMetaFromItem(this.item, this.eventTypes)
        },
        typeIcon() {
            if (this.item.content_type === 'text') return 'mdi-text'
            if (this.isTwitterEntry) return 'mdi-twitter'
            if (this.isHighlightEntry) return 'mdi-play-circle-outline'
            if (this.isVideoEntry) return 'mdi-video'
            if (this.item.content_type === 'image' || this.item.content_type === 'gallery') return 'mdi-image'
            if (this.eventMeta) return this.eventMeta.icon
            return 'mdi-circle-small'
        },
        isHighlightEntry() {
            return String(this.item.content_type || '').toLowerCase() === 'highlight'
        },
        videoPlayback() {
            return resolveVideoPlayback(this.item)
        },
        videoProviderLabel() {
            return resolveVideoProviderLabel(this.item)
        },
        videoLink() {
            return this.videoPlayback && this.videoPlayback.link ? this.videoPlayback.link : ''
        },
        videoEmbedSrc() {
            return this.videoPlayback && this.videoPlayback.kind === 'iframe' ? this.videoPlayback.src : ''
        },
        videoDirectSrc() {
            return this.videoPlayback && this.videoPlayback.kind === 'video' ? this.videoPlayback.src : ''
        },
        hasVideoEmbed() {
            return Boolean(this.videoEmbedSrc || this.videoDirectSrc)
        },
        hasVideoLink() {
            return Boolean(this.videoLink)
        },
        twitterUrl() {
            return resolveTwitterUrl(this.item)
        },
        twitterAuthor() {
            return resolveTwitterAuthor(this.item)
        },
        tweetId() {
            return resolveTweetId(this.item, this.twitterUrl)
        },
        twitterEmbedUrl() {
            if (!this.tweetId) return ''
            return `https://platform.twitter.com/embed/Tweet.html?id=${this.tweetId}&dnt=true`
        },
        hasTwitterLink() {
            return Boolean(this.twitterUrl)
        },
        hasTwitterEmbed() {
            return Boolean(this.tweetId)
        },
        resolvedImageUrls() {
            if (this.imageUrls && this.imageUrls.length) return this.imageUrls
            return this.imageUrl ? [this.imageUrl] : []
        },
        hasImages() {
            return this.resolvedImageUrls.length > 0
        },
        hasGalleryCarousel() {
            return this.resolvedImageUrls.length > 1
        },
        currentImageUrl() {
            return this.resolvedImageUrls[this.galleryIndex] || null
        },
        galleryCounter() {
            return `${this.galleryIndex + 1} / ${this.resolvedImageUrls.length}`
        },
    },
    watch: {
        resolvedImageUrls() {
            this.galleryIndex = 0
        },
        'item.event_id'() {
            this.galleryIndex = 0
        },
    },
    methods: {
        stripHtml(text) {
            if (!text) return ''
            const el = document.createElement('div')
            el.innerHTML = String(text)
            return (el.textContent || el.innerText || '').trim()
        },
        prevImage() {
            if (!this.hasGalleryCarousel) return
            this.galleryIndex =
                this.galleryIndex <= 0 ? this.resolvedImageUrls.length - 1 : this.galleryIndex - 1
        },
        nextImage() {
            if (!this.hasGalleryCarousel) return
            this.galleryIndex =
                this.galleryIndex >= this.resolvedImageUrls.length - 1 ? 0 : this.galleryIndex + 1
        },
        goToImage(index) {
            if (index < 0 || index >= this.resolvedImageUrls.length) return
            this.galleryIndex = index
        },
    },
}
</script>

<template>
    <article
        class="liveblog-timeline-item"
        :class="{ 'liveblog-timeline-item--active': active }"
    >
        <div class="liveblog-timeline-item__rail">
            <span v-if="canReorder" class="liveblog-timeline-item__drag liveblog-drag-handle" title="Drag to reorder">
                <i class="mdi mdi-drag-vertical"></i>
            </span>
            <span class="liveblog-timeline-item__minute">{{ minuteLabel }}</span>
        </div>
        <div class="liveblog-timeline-item__body">
            <div class="liveblog-timeline-item__header">
                <span class="liveblog-timeline-item__icon">
                    <i class="mdi" :class="typeIcon"></i>
                </span>
                <div class="liveblog-timeline-item__meta">
                    <div class="liveblog-timeline-item__headline">{{ headline }}</div>
                    <div v-if="subline" class="liveblog-timeline-item__subline">{{ subline }}</div>
                </div>
                <span v-if="showTypeBadge" class="badge bg-light text-dark">{{ typeBadgeLabel }}</span>
            </div>
            <div v-if="displayScore" class="liveblog-timeline-item__score">{{ scoreLabel }}</div>
            <p v-if="bodyPreview" class="liveblog-timeline-item__preview">{{ bodyPreview }}</p>

            <div v-if="hasImages" class="liveblog-timeline-item__media">
                <div class="liveblog-timeline-item__gallery-frame">
                    <button
                        v-if="hasGalleryCarousel"
                        type="button"
                        class="liveblog-timeline-item__nav liveblog-timeline-item__nav--prev"
                        title="Previous image"
                        @click="prevImage"
                    >
                        <i class="mdi mdi-chevron-left"></i>
                    </button>
                    <img :src="currentImageUrl" alt="" class="liveblog-timeline-item__image" />
                    <button
                        v-if="hasGalleryCarousel"
                        type="button"
                        class="liveblog-timeline-item__nav liveblog-timeline-item__nav--next"
                        title="Next image"
                        @click="nextImage"
                    >
                        <i class="mdi mdi-chevron-right"></i>
                    </button>
                </div>
                <div v-if="hasGalleryCarousel" class="liveblog-timeline-item__gallery-meta">
                    <span class="liveblog-timeline-item__gallery-counter">{{ galleryCounter }}</span>
                    <div class="liveblog-timeline-item__gallery-dots">
                        <button
                            v-for="(url, index) in resolvedImageUrls"
                            :key="`dot-${item.event_id}-${index}`"
                            type="button"
                            class="liveblog-timeline-item__gallery-dot"
                            :class="{ 'liveblog-timeline-item__gallery-dot--active': galleryIndex === index }"
                            :title="`Image ${index + 1}`"
                            @click="goToImage(index)"
                        />
                    </div>
                </div>
            </div>

            <div v-if="isVideoEntry" class="liveblog-timeline-item__video">
                <a
                    v-if="hasVideoLink"
                    :href="videoLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="liveblog-timeline-item__video-link"
                >
                    <i class="mdi mdi-video"></i>
                    <span>{{ videoProviderLabel || 'Open video' }}</span>
                </a>
                <iframe
                    v-if="videoEmbedSrc"
                    :key="`video-${item.event_id}-${videoEmbedSrc}`"
                    class="liveblog-timeline-item__video-frame"
                    :src="videoEmbedSrc"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowfullscreen
                    title="Embedded video"
                />
                <video
                    v-else-if="videoDirectSrc"
                    :key="`video-file-${item.event_id}-${videoDirectSrc}`"
                    class="liveblog-timeline-item__video-player"
                    :src="videoDirectSrc"
                    controls
                    playsinline
                    preload="metadata"
                />
                <p v-else class="liveblog-timeline-item__video-hint">
                    Add a Video URL or Embed ID in the video section when editing this entry.
                </p>
            </div>

            <div v-if="isTwitterEntry" class="liveblog-timeline-item__twitter">
                <a
                    v-if="hasTwitterLink"
                    :href="twitterUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="liveblog-timeline-item__twitter-link"
                >
                    <i class="mdi mdi-twitter"></i>
                    <span>{{ twitterAuthor || 'Open tweet on X' }}</span>
                </a>
                <iframe
                    v-if="hasTwitterEmbed"
                    :key="`tweet-${item.event_id}-${tweetId}`"
                    class="liveblog-timeline-item__twitter-frame"
                    :src="twitterEmbedUrl"
                    scrolling="no"
                    frameborder="0"
                    allowtransparency="true"
                    allow="encrypted-media"
                    title="Embedded tweet"
                />
                <p v-else class="liveblog-timeline-item__twitter-hint">
                    Add a Tweet URL or Tweet ID in the Twitter section when editing this entry.
                </p>
            </div>

            <div class="liveblog-timeline-item__actions">
                <button type="button" class="btn btn-sm btn-outline-primary" @click="$emit('edit', item)">
                    Edit
                </button>
                <button type="button" class="btn btn-sm btn-outline-danger" @click="$emit('delete', item.event_id)">
                    Delete
                </button>
            </div>
        </div>
    </article>
</template>

<style scoped lang="scss">
.liveblog-timeline-item {
    display: flex;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid #e8ecf1;
    border-radius: 12px;
    background: #fff;
    margin-bottom: 12px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.liveblog-timeline-item--active {
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgb(99 102 241 / 12%);
}
.liveblog-timeline-item__rail {
    flex-shrink: 0;
    width: 52px;
    text-align: center;
}
.liveblog-timeline-item__drag {
    display: block;
    color: #94a3b8;
    cursor: grab;
    margin-bottom: 4px;
}
.liveblog-timeline-item__minute {
    display: block;
    font-weight: 700;
    font-size: 1.1rem;
    color: #151a2d;
    line-height: 1.2;
}
.liveblog-timeline-item__body {
    flex: 1;
    min-width: 0;
}
.liveblog-timeline-item__header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}
.liveblog-timeline-item__icon {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f1f3f7;
    border-radius: 8px;
    font-size: 1.1rem;
    color: #151a2d;
}
.liveblog-timeline-item__meta {
    flex: 1;
    min-width: 0;
}
.liveblog-timeline-item__headline {
    font-weight: 600;
    color: #151a2d;
    line-height: 1.35;
}
.liveblog-timeline-item__subline {
    font-size: 0.8rem;
    color: #64748b;
    margin-top: 2px;
}
.liveblog-timeline-item__score {
    font-weight: 700;
    font-size: 1.25rem;
    color: #151a2d;
    margin-top: 8px;
}
.liveblog-timeline-item__preview {
    margin: 8px 0 0;
    font-size: 0.875rem;
    color: #475569;
    line-height: 1.4;
}
.liveblog-timeline-item__media {
    margin-top: 8px;
}
.liveblog-timeline-item__gallery-frame {
    position: relative;
    display: flex;
    align-items: center;
    gap: 6px;
}
.liveblog-timeline-item__image {
    flex: 1;
    min-width: 0;
    width: 100%;
    height: auto;
    max-width: 100%;
    object-fit: contain;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
}
.liveblog-timeline-item__nav {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border: 1px solid #d7dee8;
    border-radius: 999px;
    background: #fff;
    color: #151a2d;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
}
.liveblog-timeline-item__nav:hover {
    background: #f1f3f7;
    border-color: #94a3b8;
}
.liveblog-timeline-item__gallery-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 6px;
}
.liveblog-timeline-item__gallery-counter {
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 600;
}
.liveblog-timeline-item__gallery-dots {
    display: flex;
    align-items: center;
    gap: 6px;
}
.liveblog-timeline-item__gallery-dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    border: none;
    padding: 0;
    background: #cbd5e1;
    cursor: pointer;
}
.liveblog-timeline-item__gallery-dot--active {
    background: #6366f1;
}
.liveblog-timeline-item__video {
    margin-top: 8px;
}
.liveblog-timeline-item__video-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    font-weight: 600;
    color: #151a2d;
    text-decoration: none;
    margin-bottom: 8px;
}
.liveblog-timeline-item__video-link:hover {
    text-decoration: underline;
}
.liveblog-timeline-item__video-frame,
.liveblog-timeline-item__video-player {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #000;
}
.liveblog-timeline-item__video-player {
    object-fit: contain;
}
.liveblog-timeline-item__video-hint {
    margin: 8px 0 0;
    font-size: 0.8rem;
    color: #64748b;
}
.liveblog-timeline-item__twitter {
    margin-top: 8px;
}
.liveblog-timeline-item__twitter-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    font-weight: 600;
    color: #1d9bf0;
    text-decoration: none;
    margin-bottom: 8px;
}
.liveblog-timeline-item__twitter-link:hover {
    text-decoration: underline;
}
.liveblog-timeline-item__twitter-frame {
    display: block;
    width: 100%;
    min-height: 600px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    margin-top: 8px;
    background: #fff;
}
.liveblog-timeline-item__twitter-hint {
    margin: 8px 0 0;
    font-size: 0.8rem;
    color: #64748b;
}
.liveblog-timeline-item__actions {
    display: flex;
    gap: 8px;
    margin-top: 10px;
}
.liveblog-sortable-ghost .liveblog-timeline-item {
    opacity: 0.45;
    background: #f8fafc;
}
</style>
